import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, notificationApi } from '../api';

interface CardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  color: string;
  icon: string;
  onClick?: () => void;
}

function StatCard({ title, value, subtitle, color, icon, onClick }: CardProps) {
  return (
    <div
      className={`bg-white rounded-xl shadow-sm p-4 border border-slate-100 hover:shadow-md transition cursor-pointer ${onClick ? 'hover:border-brand-200' : ''}`}
      onClick={onClick}
    >
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs text-slate-500">{title}</div>
          <div className={`text-2xl font-bold mt-1 ${color}`}>{value}</div>
          {subtitle && <div className="text-[11px] text-slate-400 mt-0.5">{subtitle}</div>}
        </div>
        <span className="text-2xl opacity-60">{icon}</span>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const nav = useNavigate();
  const [finance, setFinance] = useState<any>(null);
  const [salesCount, setSalesCount] = useState(0);
  const [stockValue, setStockValue] = useState(0);
  const [custCount, setCustCount] = useState(0);
  const [prodCount, setProdCount] = useState(0);
  const [todayAtt, setTodayAtt] = useState(0);
  const [unread, setUnread] = useState(0);
  const [pendingSales, setPendingSales] = useState(0);
  const [pendingCust, setPendingCust] = useState(0);

  useEffect(() => {
    api('/finance/summary').then(setFinance).catch(() => {});
    api('/sales/orders').then((d: any[]) => { setSalesCount(d.length); setPendingSales(d.filter((o) => o.status === 'pending').length); }).catch(() => {});
    api('/inventory/stock').then((d: any[]) => {
      setStockValue(d.reduce((s, p) => s + (p.current || 0) * (p.cost || 0), 0));
      setProdCount(d.length);
    }).catch(() => {});
    api('/customer/customers').then((d: any[]) => setCustCount(d.length)).catch(() => {});
    api('/customer/orders').then((d: any[]) => setPendingCust(d.filter((o: any) => o.status === 'pending').length)).catch(() => {});
    api('/attendance/stats').then((d: any) => setTodayAtt(d.todayCount || 0)).catch(() => {});
    notificationApi.count().then((d: any) => setUnread(d.unread)).catch(() => {});
  }, []);

  // 快捷入口卡片
  const shortcuts = [
    { title: '销售报货', desc: '新建/审核销售订单', icon: '🛒', to: '/sales', color: 'bg-blue-50 text-blue-600' },
    { title: '客户报货', desc: '客户下单→后勤闭环', icon: '👥', to: '/customer', color: 'bg-indigo-50 text-indigo-600' },
    { title: '进出库存', desc: '入库/出库/盘点', icon: '📦', to: '/inventory', color: 'bg-orange-50 text-orange-600' },
    { title: '生产管理', desc: '生产单→完工入库', icon: '🏭', to: '/production', color: 'bg-purple-50 text-purple-600' },
    { title: '考勤打卡', desc: '签到/签退/工时统计', icon: '⏰', to: '/attendance', color: 'bg-teal-50 text-teal-600' },
    { title: '产品管理', desc: '产品档案与销量排行', icon: '📋', to: '/products', color: 'bg-green-50 text-green-600' },
    { title: '财务统计', desc: '收支记账与利润分析', icon: '💰', to: '/finance', color: 'bg-yellow-50 text-yellow-600' },
    { title: '操作日志', desc: '审计追踪与备份', icon: '📝', to: '/logs', color: 'bg-red-50 text-red-600' },
  ];

  return (
    <div className="space-y-6">
      {/* 欢迎区 */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">经营仪表盘</h1>
          <p className="text-sm text-slate-500 mt-1">实时掌握企业运营全貌</p>
        </div>
        <div className="text-xs text-slate-400">{new Date().toLocaleDateString('zh-CN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</div>
      </div>

      {/* 核心指标 */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <StatCard title="总收入" value={finance ? `¥${finance.totalIncome?.toLocaleString()}` : '...'} color="text-green-600" icon="📈" onClick={() => nav('/finance')} subtitle="累计收入" />
        <StatCard title="总支出" value={finance ? `¥${finance.totalExpense?.toLocaleString()}` : '...'} color="text-red-500" icon="📉" onClick={() => nav('/finance')} subtitle="累计支出" />
        <StatCard title="净利润" value={finance ? `¥${finance.profit?.toLocaleString()}` : '...'} color="text-blue-600" icon="💎" onClick={() => nav('/finance')} subtitle="收入 - 支出" />
        <StatCard title="库存总值" value={`¥${stockValue.toLocaleString()}`} color="text-orange-600" icon="📦" onClick={() => nav('/inventory')} subtitle={`${prodCount} 种产品`} />
        <StatCard title="今日出勤" value={todayAtt} color="text-teal-600" icon="⏰" onClick={() => nav('/attendance')} subtitle="人" />
      </div>

      {/* 业务概览行 */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard title="销售订单" value={salesCount} color="text-blue-600" icon="🛒" onClick={() => nav('/sales')} subtitle="全部订单" />
        <StatCard title="客户数量" value={custCount} color="text-indigo-600" icon="👥" onClick={() => nav('/customer')} subtitle="家" />
        <StatCard title="产品SKU" value={prodCount} color="text-green-600" icon="📋" onClick={() => nav('/products')} subtitle="种" />
        <StatCard title="利润率" value={finance?.totalIncome ? ((finance.profit / finance.totalIncome) * 100).toFixed(1) + '%' : '...'} color="text-purple-600" icon="📊" subtitle="净利润 / 总收入" />
      </div>

      {/* 快捷入口 */}
      <div>
        <h2 className="text-lg font-bold mb-3">⚡ 快捷操作</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {shortcuts.map((s) => (
            <button
              key={s.to}
              onClick={() => nav(s.to)}
              className={`rounded-xl p-4 text-left hover:shadow-md transition border border-transparent hover:border-slate-200 ${s.color}`}
            >
              <span className="text-2xl">{s.icon}</span>
              <div className="font-medium mt-2 text-sm">{s.title}</div>
              <div className="text-xs opacity-70 mt-0.5">{s.desc}</div>
            </button>
          ))}
        </div>
      </div>

      {/* 待办提醒（真实数据） */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
        <h3 className="font-bold text-sm text-amber-800 mb-2">💡 今日待办</h3>
        <ul className="text-sm text-amber-700 space-y-1">
          <li className="flex items-center justify-between">
            <span>• <button className="font-medium underline" onClick={() => nav('/notifications')}>未读消息</button></span>
            <span className="font-bold">{unread} 条</span>
          </li>
          <li className="flex items-center justify-between">
            <span>• <span className="font-medium">待审核</span>销售订单</span>
            <span className="font-bold">{pendingSales} 笔</span>
          </li>
          <li className="flex items-center justify-between">
            <span>• <span className="font-medium">待接单</span>客户报货</span>
            <span className="font-bold">{pendingCust} 笔</span>
          </li>
          <li>• 关注<span className="font-medium">低库存预警</span>产品，及时安排生产或采购</li>
          <li>• 确认今日<span className="font-medium">考勤打卡</span>情况</li>
        </ul>
      </div>
    </div>
  );
}
